import {
  Assessment,
  AssessmentWithUser,
} from "@appally/collections/Assessment";
import {
  AssessmentProductStatus,
  AssessmentProductWithProducts,
} from "@appally/collections/AssessmentProduct";
import {
  AssessmentSelfStrategyStatus,
  AssessmentSelfStrategyWithSelfStrategyAndTasks,
} from "@appally/collections/AssessmentSelfStrategy";
import {
  AssessmentSolutionStatus,
  AssessmentSolutionWithSolutions,
} from "@appally/collections/AssessmentSolution";
import { AssessmentStats } from "@appally/collections/AssessmentStats";
import { OrganisationPublic } from "@appally/collections/OrganisationPublic";
import { Product } from "@appally/collections/Product";
import {
  AssessmentArchivedReport,
  AssessmentReport,
} from "@appally/collections/Report";
import { SelfStrategy } from "@appally/collections/SelfStrategy";
import { Task } from "@appally/collections/Task";
import { collections } from "@appally/lib/data/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { doc, getDoc, getDocs, query, setDoc } from "firebase/firestore";
import { flatten, orderBy } from "lodash";
import {
  assessmentsWithAdditionalFields,
  confirmMyAssessmentHelper,
  getAndVerifyAssessmentSolutions,
  getAssessment,
  getCurrentUsersAssessment,
  updateAssessment,
  UpdateMyAssessmentInput,
} from "../actions/assessment";
import { getCurrentUser, getUser } from "../actions/user";

export type UpdateAssessmentSolutionPayload = {
  assessmentId: string;
  solutionId: string;
  status: AssessmentSolutionStatus;
  rejected_reason: string | null;
  tasksToToggle?: string[];
  manager_responded_at?: number;
};

export type UpdateAssessmentProductPayload = {
  assessmentId: string;
  productId: string;
  status: AssessmentProductStatus;
};

export type UpdateAssessmentSelfStrategiesPayload = {
  assessmentId: string;
  selfStrategyId: string;
  status: AssessmentSelfStrategyStatus;
};

// Define a service using a base URL and expected endpoints
export const assessmentApi = createApi({
  reducerPath: "assessments",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "Assessments",
    "AssessmentStats",
    "Solutions",
    "Products",
    "Reports",
    "SelfStrategies",
    "OrganisationPublic",
  ],
  endpoints: (builder) => ({
    // Get all the assessments that this user has access to
    getAssessments: builder.query<Assessment[], void>({
      queryFn: async () => {
        // Get the current user
        const currentUser = await getCurrentUser();
        if (!currentUser) return { data: [] };

        // Get the assessment for this user
        const myAssessment = await getCurrentUsersAssessment();
        const data = [myAssessment].map((doc) =>
          assessmentsWithAdditionalFields(doc!),
        );

        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "Assessments" as const,
                id,
              })),
              { type: "Assessments", id: "LIST" },
            ]
          : [{ type: "Assessments", id: "LIST" }],
    }),

    // Get the current user's assessment
    getAssessment: builder.query<AssessmentWithUser, string | void>({
      queryFn: async (id) => {
        const data = id
          ? await getAssessment(id)
          : await getCurrentUsersAssessment();
        if (!data) {
          if (id) {
            throw new Error(
              "You don't have access to the assessment with the id: " + id,
            );
          } else {
            throw new Error("You don't have access to your assessment");
          }
        }
        const user = await getUser(data.assessment_user);
        return {
          data: {
            ...data,
            user,
          },
        };
      },
      providesTags: (result) =>
        result ? [{ type: "Assessments", id: result.id }] : [],
    }),

    // Get the assessment's stats
    getAssessmentStats: builder.query<AssessmentStats | undefined, string>({
      queryFn: async (id) => {
        const assessment = await getAssessment(id);
        if (!assessment)
          throw new Error(
            "You don't have access to this assessment's information",
          );
        const thisDoc = await getDoc(doc(collections.assessment_stats, id));
        if (!thisDoc.exists()) {
          return { data: undefined };
        }
        const data = thisDoc.data();
        return {
          data: {
            ...data,
            id,
          },
        };
      },
      providesTags: (result) =>
        result ? [{ type: "AssessmentStats", id: result.id }] : [],
    }),

    // Get the public organisation details for this assessment
    getAssessmentOrganisationPublic: builder.query<
      OrganisationPublic | null,
      string
    >({
      queryFn: async (id) => {
        const assessment = await getAssessment(id);
        if (!assessment) {
          throw new Error(
            "You don't have access to this assessment's organisation",
          );
        }
        if (!assessment.organisation_id) return { data: null };

        const thisDoc = await getDoc(
          doc(collections.organisation_public, assessment.organisation_id),
        );

        return { data: thisDoc.exists() ? thisDoc.data() : null };
      },
      providesTags: (result, error, id) => [
        { type: "OrganisationPublic", id },
      ],
    }),

    // Update my assessment
    updateAssessment: builder.mutation<Assessment, UpdateMyAssessmentInput>({
      queryFn: async (input) => {
        const data = await updateAssessment(input);
        return { data };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "Assessments", id: result.id }] : [],
    }),

    // Confirm the assessment helper to join my team
    confirmMyAssessmentHelper: builder.mutation<Assessment, void>({
      queryFn: async (_, api) => {
        const data = await confirmMyAssessmentHelper();
        return { data };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "Assessments", id: result.id }] : [],
    }),

    // Get the current user's solutions
    getAssessmentSolutions: builder.query<
      AssessmentSolutionWithSolutions[],
      string
    >({
      providesTags: ["Solutions"],
      queryFn: async (id) => {
        const data = await getAndVerifyAssessmentSolutions(id);
        return { data };
      },
    }),

    // Get the current user's solutions
    updateAssessmentSolution: builder.mutation<
      boolean,
      UpdateAssessmentSolutionPayload
    >({
      invalidatesTags: ["Solutions"],
      queryFn: async (input) => {
        // Get the current user's assessment solutions
        const assessment = await getAssessment(input.assessmentId);
        if (!assessment)
          throw new Error(
            "You don't have access to update this assessment's recommendations",
          );
        const thisDoc = await getDoc(
          doc(collections.assessment_solutions, input.assessmentId),
        );
        if (!thisDoc.exists()) {
          throw new Error("Assessment solutions not found");
        }
        const assessmentSolution = thisDoc.data();

        // Set the solution data
        const solution = assessmentSolution.solutions[input.solutionId];
        solution.status = input.status;
        solution.requested_at = Date.now();
        if (input.status === AssessmentSolutionStatus.USER_REJECTED) {
          solution.rejected_reason = input.rejected_reason;
        } else {
          solution.rejected_reason = null;
        }
        if (input.manager_responded_at) {
          solution.manager_responded_at = input.manager_responded_at;
        }

        // Update the tasks if needed
        if (input.tasksToToggle?.length) {
          input.tasksToToggle.forEach((taskId) => {
            const task = Object.entries(solution.tasks).find(
              ([key, t]) => t.task_id === taskId,
            );
            if (!task) return;
            const index = parseInt(task[0]);
            if (solution.tasks[index].completed_at) {
              solution.tasks[index].completed_at = null;
            } else {
              solution.tasks[index].completed_at = Date.now();
            }
          });
        }

        // Update the solution
        await setDoc(
          doc(collections.assessment_solutions, assessment.id),
          {
            solutions: {
              ...assessmentSolution.solutions,
              [input.solutionId]: solution,
            },
          },
          { merge: true },
        );

        return {
          data: true,
        };
      },
    }),

    // Get the current user's products
    getAssessmentProducts: builder.query<
      AssessmentProductWithProducts[],
      string
    >({
      providesTags: ["Products"],
      queryFn: async (id) => {
        const assessment = await getAssessment(id);
        if (!assessment)
          throw new Error(
            "You don't have access to this assessment's products",
          );

        // Get the assessment products
        const thisDoc = await getDoc(doc(collections.assessment_products, id));
        if (!thisDoc.exists()) return { data: [] };
        const assessmentProduct = thisDoc.data();

        // Get the assessment solutions
        const solutions = await getAndVerifyAssessmentSolutions(id);

        // Get a list of all productIds that we need to match from the selected solutions
        const allowedProductIds = flatten(
          solutions
            .filter((s) => s.status === AssessmentSolutionStatus.APPROVED)
            .map((solution) => {
              return Object.values(solution.solution.product_id_array ?? {});
            }),
        );

        // Get the products
        const productIds = Object.keys(
          assessmentProduct.products,
        ) as Product["id"][];
        const productsDocs = await getDocs(query(collections.products));
        const products = productsDocs.docs.map((doc) => doc.data());

        // Add the products to the objects
        const data = orderBy(
          productIds
            .filter((prodId) => {
              return allowedProductIds.includes(prodId);
            })
            .map((productId) => {
              const product = products.find((sol) => sol.id === productId);
              if (!product) return null;
              const obj = assessmentProduct.products[productId];
              return {
                ...obj,
                product,
              } satisfies AssessmentProductWithProducts;
            })
            .filter((s) => !!s),
          ["final_score", "product.id"],
          ["desc", "asc"],
        );

        return { data };
      },
    }),

    // Update the current user's products
    updateAssessmentProducts: builder.mutation<
      boolean,
      UpdateAssessmentProductPayload
    >({
      invalidatesTags: ["Products"],
      queryFn: async (input) => {
        // Get the current user's assessment products
        const assessment = await getAssessment(input.assessmentId);
        if (!assessment)
          throw new Error(
            "You don't have access to update this assessment's products",
          );
        const thisDoc = await getDoc(
          doc(collections.assessment_products, input.assessmentId),
        );
        if (!thisDoc.exists()) {
          throw new Error("Assessment products not found");
        }
        const assessmentProduct = thisDoc.data();

        // Set the product data
        const solution = assessmentProduct.products[input.productId];
        solution.status = input.status;

        // Update the product
        await setDoc(
          doc(collections.assessment_products, assessment.id),
          {
            products: {
              ...assessmentProduct.products,
              [input.productId]: solution,
            },
          },
          { merge: true },
        );

        return {
          data: true,
        };
      },
    }),

    // Get the current user's self-strategies
    getAssessmentSelfStrategies: builder.query<
      AssessmentSelfStrategyWithSelfStrategyAndTasks[],
      string
    >({
      providesTags: ["SelfStrategies"],
      queryFn: async (id) => {
        const assessment = await getAssessment(id);
        if (!assessment)
          throw new Error(
            "You don't have access to this assessment's self-strategies",
          );

        // Get the assessment strategies
        const thisDoc = await getDoc(
          doc(collections.assessment_self_strategies, id),
        );
        if (!thisDoc.exists()) return { data: [] };
        const assessmentStrategies = thisDoc.data();

        const strategyIds = Object.keys(
          assessmentStrategies.self_strategies,
        ) as SelfStrategy["id"][];

        const strategiesDocs = await Promise.all(
          strategyIds.map((strategyId) =>
            getDoc(doc(collections.self_strategies, strategyId)),
          ),
        );
        const strategies = strategiesDocs
          .filter((strategyDoc) => strategyDoc.exists())
          .map((strategyDoc) => strategyDoc.data());

        const taskIds = [
          ...new Set(
            Object.values(assessmentStrategies.self_strategies).flatMap(
              (strategy) =>
                Object.values(strategy.tasks).map((task) => task.task_id),
            ),
          ),
        ] as Task["id"][];

        const tasksDocs = await Promise.all(
          taskIds.map((taskId) => getDoc(doc(collections.tasks, taskId))),
        );
        const tasks = tasksDocs
          .filter((taskDoc) => taskDoc.exists())
          .map((taskDoc) => taskDoc.data());

        // Add the strategies to the objects
        const data = orderBy(
          strategyIds
            .map((strategyId) => {
              const self_strategy = strategies.find((s) => s.id === strategyId);
              if (!self_strategy) return null;
              const stats = assessmentStrategies.self_strategies[strategyId];

              const newObj: AssessmentSelfStrategyWithSelfStrategyAndTasks = {
                ...stats,
                self_strategy: self_strategy,
                tasks: {},
              };

              for (const key in stats.tasks) {
                const taskId = stats.tasks[key].task_id;
                const task = tasks.find((t) => t.id === taskId);
                if (task) {
                  newObj.tasks[key] = {
                    task_id: taskId,
                    completed_at: stats.tasks[key].completed_at,
                    task_order:
                      stats.tasks[key].task_order ?? parseInt(key, 10),
                    task: task,
                  };
                }
              }

              return newObj;
            })
            .filter((s) => !!s),
          ["final_score", "self_strategy.id"],
          ["desc", "asc"],
        );

        return { data };
      },
    }),

    // Update the current user's products
    updateAssessmentSelfStrategies: builder.mutation<
      boolean,
      UpdateAssessmentSelfStrategiesPayload
    >({
      invalidatesTags: ["SelfStrategies"],
      queryFn: async (input) => {
        // Get the current user's assessment self-strategies
        const assessment = await getAssessment(input.assessmentId);
        if (!assessment)
          throw new Error(
            "You don't have access to update this assessment's products",
          );

        const thisDoc = await getDoc(
          doc(collections.assessment_self_strategies, input.assessmentId),
        );
        if (!thisDoc.exists()) {
          throw new Error("Assessment self-strategies not found");
        }
        const assessmentSelfStrategies = thisDoc.data();

        // Set the self-strategy data
        const selfStrategy =
          assessmentSelfStrategies.self_strategies[input.selfStrategyId];
        selfStrategy.status = input.status;

        // Update the product
        await setDoc(
          doc(collections.assessment_self_strategies, assessment.id),
          {
            self_strategies: {
              ...assessmentSelfStrategies.self_strategies,
              [input.selfStrategyId]: selfStrategy,
            },
          },
          { merge: true },
        );

        return {
          data: true,
        };
      },
    }),

    // Get the current user's reports
    getAssessmentReport: builder.query<AssessmentReport | undefined, string>({
      providesTags: ["Reports"],
      queryFn: async (id) => {
        const assessment = await getAssessment(id);
        if (!assessment)
          throw new Error("You don't have access to this assessment's reports");

        // Get the assessment reports
        const thisDoc = await getDoc(doc(collections.reports, id));
        if (!thisDoc.exists()) {
          return { data: undefined };
        }
        const data = thisDoc.data();
        return { data };
      },
    }),

    // Update a report
    updateAssessmentReport: builder.mutation<
      boolean,
      { assessmentId: string; generating: boolean }
    >({
      queryFn: async (input) => {
        // Get the current user's assessment products
        const assessment = await getAssessment(input.assessmentId);
        if (!assessment)
          throw new Error(
            "You don't have access to update this assessment's reports",
          );

        // Update the report
        await setDoc(
          doc(collections.reports, assessment.id),
          {
            generating: input.generating,
          },
          { merge: true },
        );

        return {
          data: true,
        };
      },
      invalidatesTags: (result) => (result ? [{ type: "Reports" }] : []),
    }),

    // Get the current user's reports
    listAssessmentReports: builder.query<
      AssessmentArchivedReport | undefined,
      string
    >({
      providesTags: ["Reports"],
      queryFn: async (id) => {
        const assessment = await getAssessment(id);
        if (!assessment)
          throw new Error(
            "You don't have access to this assessment's archived reports",
          );

        // Get the assessment reports
        const thisDoc = await getDoc(doc(collections.archived_reports, id));
        if (!thisDoc.exists()) {
          return { data: undefined };
        }
        const data = thisDoc.data();
        return { data };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAssessmentQuery,
  useGetAssessmentsQuery,
  useUpdateAssessmentMutation,
  useGetAssessmentProductsQuery,
  useGetAssessmentSolutionsQuery,
  useGetAssessmentOrganisationPublicQuery,
  useGetAssessmentStatsQuery,
  useConfirmMyAssessmentHelperMutation,
  useUpdateAssessmentSolutionMutation,
  useUpdateAssessmentProductsMutation,
  useGetAssessmentSelfStrategiesQuery,
  useUpdateAssessmentSelfStrategiesMutation,
  useGetAssessmentReportQuery,
  useListAssessmentReportsQuery,
  useUpdateAssessmentReportMutation,
} = assessmentApi;
